/* ============================================================================
  provisions-quiz.js  (Shopify OS 2.0 + Shopify Subscriptions)

  ✅ Variant-based pricing (matches Shopify variants):
    Option 1: Pick your Kit = Artisan, Deluxe
    Option 2: Wine Pairing = None, Standard, Premium
    Option 3: Shipping = In-Store Pickup, California Shipping, Non-CA Shipping

  Includes:
  - Intro screen (hero)
  - Updated “What excites you…” step with 2-column image + stacked options
  - Category tiles with scene images (from #pc-image-scenes JSON)
  - Flavor sliders optional (default "Balanced")
  - Basket selection (Artisan/Deluxe) + Wine pairing question
  - Cadence step shows incentive messaging + estimated totals (discount display)
  - Delivery method + follow-up (Ship vs Pickup SF locations)
  - Summary step w/ variant price + confirm, then adds correct variant + selling plan
  - Left step column with completed checkmarks (chips on mobile)
  - ✅ “Continue” always visible (sticky actions). Long steps scroll INSIDE the card.
============================================================================ */

(() => {
  const root = document.getElementById("pc-quiz");
  const ui = document.getElementById("pc-quiz-ui");
  if (!root || !ui) return;

  const PRODUCT_HANDLE = "provisions-club";

  // NOTE: This is only a fallback. We resolve the REAL variant from quiz answers.
  const DEFAULT_VARIANT_ID = Number(root.dataset.variantId || 0);

  const SELLING_PLANS = (() => {
    try {
      return JSON.parse(root.dataset.sellingPlans || "{}");
    } catch {
      return {};
    }
  })();

  const SHOPIFY_ROOT =
    window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : "/";

  const escapeHtml = (s) =>
    String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

  function getSellingPlanIdFromCadence(cadence) {
    const id = Number((SELLING_PLANS && SELLING_PLANS[cadence]) || 0);
    if (!id) throw new Error(`Missing selling plan ID for cadence "${cadence}".`);
    return id;
  }

  const DEFAULT_SCENES = [];
  const SCENES = (() => {
    try {
      const script = document.getElementById("pc-image-scenes");
      if (!script) return DEFAULT_SCENES;
      const parsed = JSON.parse(script.textContent || "[]");
      return Array.isArray(parsed) ? parsed : DEFAULT_SCENES;
    } catch (e) {
      console.error("Provisions Quiz: failed to parse scenes JSON", e);
      return DEFAULT_SCENES;
    }
  })();

  async function postJSON(path, payload) {
    const res = await fetch(`${SHOPIFY_ROOT}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Request failed: ${path} (${res.status}) ${text}`.slice(0, 300));
    }
    return res.json();
  }

  async function clearCart() {
    return postJSON("cart/clear.js", {});
  }
  async function setCartAttributes(attributes, note = "") {
    return postJSON("cart/update.js", { attributes, note });
  }
  async function addSubscriptionItem(variantId, sellingPlanId, quantity = 1) {
    return postJSON("cart/add.js", {
      items: [
        {
          id: Number(variantId),
          quantity: Number(quantity),
          selling_plan: Number(sellingPlanId),
        },
      ],
    });
  }

  // ---- Money helpers
  function formatMoneyFromCents(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return "";
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        const fmt = window.Shopify.money_format || "{{amount}}";
        return window.Shopify.formatMoney(n, fmt);
      }
    } catch {}
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD",
    }).format(n / 100);
  }

  // ---- Variant resolution (CRITICAL for correct pricing)
  let _productCache = null;
  async function fetchProductJSON() {
    if (_productCache) return _productCache;
    const res = await fetch(`${SHOPIFY_ROOT}products/${PRODUCT_HANDLE}.js`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("Could not load product JSON for variant resolution.");
    _productCache = await res.json();
    return _productCache;
  }

  function getChosenOptionValues() {
    // Option 1: Pick your Kit
    const kit =
      state.budget_tier === "deluxe" ? "Deluxe" :
      state.budget_tier === "artisan" ? "Artisan" :
      "";

    // Option 2: Wine Pairing
    const wine =
      state.wine_pairing === "premium" ? "Premium" :
      state.wine_pairing === "standard" ? "Standard" :
      "None";

    // Option 3: Shipping
    let shipping = "";
    if (state.delivery_method === "pickup") shipping = "In-Store Pickup";
    if (state.delivery_method === "ship" && state.ca_residency === "in_ca") shipping = "California Shipping";
    if (state.delivery_method === "ship" && state.ca_residency === "outside_ca") shipping = "Non-CA Shipping";

    return { kit, wine, shipping };
  }

  async function resolveSelectedVariant() {
    const product = await fetchProductJSON();
    const { kit, wine, shipping } = getChosenOptionValues();

    // Only resolve when required choices exist
    if (!kit || !wine || !shipping) return null;

    const optionNames = (product.options || []).map((o) => o.name || "");

    const idxKit = optionNames.findIndex((n) => n.toLowerCase().includes("kit"));
    const idxWine = optionNames.findIndex((n) => n.toLowerCase().includes("wine"));
    const idxShip = optionNames.findIndex(
      (n) => n.toLowerCase().includes("ship") || n.toLowerCase().includes("pickup")
    );

    const variants = product.variants || [];
    let variant = null;

    // Match by names if possible (safest)
    if (idxKit >= 0 && idxWine >= 0 && idxShip >= 0) {
      variant =
        variants.find((v) => {
          const opts = v.options || [];
          return opts[idxKit] === kit && opts[idxWine] === wine && opts[idxShip] === shipping;
        }) || null;
    }

    // Fallback: assume option positions 1/2/3
    if (!variant) {
      variant =
        variants.find((v) => v.option1 === kit && v.option2 === wine && v.option3 === shipping) || null;
    }

    if (!variant) {
      state.selected_variant_id = 0;
      state.selected_variant_price_cents = null;
      return null;
    }

    state.selected_variant_id = Number(variant.id);
    state.selected_variant_price_cents = Number(variant.price);
    return variant;
  }

  // ---- Pricing display constants (display only; cart pricing comes from variant + selling plan)
  const PRICING = {
    base_cents: { artisan: 13000, deluxe: 18000 },
    wine_cents: { none: 0, standard: 2000, premium: 4500 },
    ship_cents: { pickup: 0, in_ca: 1000, outside_ca: 1500 },
    cadence_discount_pct: { monthly: 10, bi_monthly: 5, quarterly: 0 },
  };

  function computeDisplayTotalCents() {
    const kitKey = state.budget_tier || "";
    const wineKey = state.wine_pairing || "none";
    const shipKey =
      state.delivery_method === "pickup"
        ? "pickup"
        : state.delivery_method === "ship" && state.ca_residency === "in_ca"
          ? "in_ca"
          : state.delivery_method === "ship" && state.ca_residency === "outside_ca"
            ? "outside_ca"
            : "";

    const base = PRICING.base_cents[kitKey] ?? 0;
    const wine = PRICING.wine_cents[wineKey] ?? 0;
    const ship = PRICING.ship_cents[shipKey] ?? 0;

    const subtotal = base + wine + ship;
    const pct = PRICING.cadence_discount_pct[state.cadence] ?? 0;
    const discount = Math.round(subtotal * (pct / 100));
    const total = subtotal - discount;

    return { base, wine, ship, subtotal, discount, total, pct };
  }

  // ---- State
  const state = {
    curated_for: "",
    goals: [],
    categories: [],
    flavor: { sweet_savory: "Balanced", bold_mild: "Balanced" },
    spice: "",
    dietary: [],
    avoid_ingredients: [],
    avoid_types: [],
    adventure: 3,

    // Basket + wine
    budget_tier: "",        // artisan | deluxe
    wine_pairing: "none",   // none | standard | premium

    cadence: "monthly",
    visual_feedback: [],
    notes: "",

    delivery_method: "", // ship | pickup
    ca_residency: "", // in_ca | outside_ca
    pickup_location: "", // location name

    // variant chosen from option values
    selected_variant_id: 0,
    selected_variant_price_cents: null,

    // summary
    summary_confirm: false,
  };

  // ---- Options
  const OPTIONS = {
    curatedFor: [
      { value: "solo", label: "Just me", sub: "Personalized to your taste." },
      { value: "household", label: "My household", sub: "Balanced for more than one palate." },
      { value: "gift", label: "Gifting someone", sub: "Giftable picks with wow factor." },
    ],

    goals: [
      { id: "stocked", label: "Keeping my pantry stocked", sub: "Everyday essentials I’ll reach for." },
      { id: "discover", label: "Discovering new flavors and tastes", sub: "Small-batch finds + new favorites." },
      { id: "save_time", label: "Saving time at the market", sub: "Skip the browsing—get the good stuff." },
      { id: "entertain", label: "Indulgences for entertaining", sub: "Host-ready picks with wow factor." },
      { id: "gift", label: "A special gift", sub: "Thoughtful, elevated, and easy." },
      { id: "other", label: "Something else", sub: "Tell us what you’re hoping for later." },
    ],

    categories: [
      { id: "pantry", label: "Pantry staples", sub: "Olive oil, vinegar, salts" },
      { id: "snacks", label: "Snacks & sweets", sub: "Chocolate, cookies, crunchy" },
      { id: "charcuterie", label: "Cheese board accompaniments", sub: "Jams, crackers, spreads" },
      { id: "pasta", label: "Pasta, grains & sauces", sub: "Weeknight heroes" },
      { id: "tinned", label: "Tinned seafood", sub: "Coastal pantry-cool" },
      { id: "condiments", label: "Condiments & spreads", sub: "Sauces, mustards, chili crisp" },
      { id: "na_bev", label: "Non-alcoholic beverages", sub: "Sparkling, bitters, mixers" },
      { id: "home", label: "Kitchen + table goods", sub: "Tools + tabletop upgrades" },
      { id: "seasonal", label: "Seasonal / limited editions", sub: "Special drops" },
    ],

    spice: [
      { value: "none", label: "No heat" },
      { value: "mild", label: "Mild" },
      { value: "medium", label: "Medium" },
      { value: "hot", label: "Hot" },
      { value: "fire", label: "Bring the fire" },
    ],

    dietary: [
      "Vegetarian",
      "Vegan",
      "Pescatarian",
      "Gluten-free",
      "Dairy-free",
      "Nut-free",
      "Low sugar",
      "No pork",
      "No shellfish",
      "No alcohol-based products",
      "None / I’m flexible",
    ],

    avoidIngredients: [
      "Anchovies",
      "Mushrooms",
      "Olives",
      "Truffles",
      "Licorice",
      "Coconut",
      "Artificial sweeteners",
      "Strong smoke flavors",
    ],

    avoidTypes: [
      "Sweets",
      "Spicy items",
      "Preserved fish",
      "Condiments",
      "Crackers / carbs",
      "Kitchen tools",
      "Serveware",
      "Giftable items",
    ],

    baskets: [
      {
        value: "artisan",
        label: "Artisan",
        sub: "Starts at $130 per shipment • 7–12 items",
      },
      {
        value: "deluxe",
        label: "Deluxe",
        sub: "Starts at $180 per shipment • 10–15 items",
      },
    ],

    wine: [
      { value: "none", label: "No wine pairing", sub: "Skip wine for now." },
      { value: "standard", label: "Standard wine pairing", sub: "Add $20 per shipment." },
      { value: "premium", label: "Premium wine pairing", sub: "Add $45 per shipment." },
    ],

    cadence: [
      { value: "monthly", label: "Every month", sub: "Save 10%" },
      { value: "bi_monthly", label: "Every 2 months", sub: "Save 5%" },
      { value: "quarterly", label: "Every 3 months", sub: "No additional discount" },
    ],

    deliveryMethod: [
      { value: "ship", label: "Ship it to me", sub: "Delivered to your door." },
      { value: "pickup", label: "Pick up in San Francisco", sub: "Choose one of our five locations." },
    ],

    caResidency: [
      { value: "in_ca", label: "In California", sub: "Adds $10 shipping per shipment." },
      { value: "outside_ca", label: "Outside California", sub: "Adds $15 shipping per shipment." },
    ],

    pickupLocations: [
      { value: "Bernal Heights", label: "Bernal Heights" },
      { value: "Hayes Valley", label: "Hayes Valley" },
      { value: "Cow Hollow/Marina", label: "Cow Hollow / Marina" },
      { value: "Castro", label: "Castro" },
      { value: "The Ferry Building", label: "The Ferry Building" },
    ],
  };

  // ---- Intro content
  const INTRO = {
    hero:
      "https://cdn.shopify.com/s/files/1/2515/7194/files/provisions-club-809840.jpg?v=1752539534",
    kicker: "THE PROVISIONS CLUB",
    title: "A subscription box of exceptional provisions—curated for your taste.",
    bullets: [
      "Small-batch pantry goods, snacks, and table finds—picked by our team.",
      "Personalized to your preferences (sweet/savory, bold/mild, dietary needs).",
      "Flexible cadence. Skip or cancel anytime.",
    ],
    note: "Take this quick quiz and we’ll build your Provisions Club profile.",
  };

  // ---- Steps
  const steps = [
    { id: "intro", title: "Overview", desc: "", render: () => introScreen(), valid: () => true, optional: true },

    {
      id: "curated_for",
      title: "Who are we curating for?",
      desc: "This helps us tailor portions and picks.",
      render: () => tileRadio("curated_for", OPTIONS.curatedFor, state.curated_for),
      valid: () => !!state.curated_for,
    },

    {
      id: "goals",
      title: "What gets you most excited about the Provisions Club?",
      desc: "Pick as many as you’d like — we’ll personalize around your priorities.",
      render: () =>
        twoColumnImageMulti(
          "goals",
          OPTIONS.goals,
          state.goals,
          "https://cdn.shopify.com/s/files/1/2515/7194/files/Provisions_Club_Intro.png?v=1768508512"
        ),
      valid: () => state.goals.length >= 1,
    },

    {
      id: "categories",
      title: "What sounds good right now?",
      desc: "Pick up to 5.",
      render: () => tileMultiWithSceneImages("categories", OPTIONS.categories, state.categories, 5),
      valid: () => state.categories.length >= 1,
    },

    {
      id: "flavor",
      title: "Dial in your taste",
      desc: "Optional — you can leave these as balanced.",
      render: () => flavorScreen(),
      valid: () => true,
      optional: true,
    },

    {
      id: "dietary",
      title: "Any dietary needs?",
      desc: "Optional — we’ll always honor these.",
      render: () => chipMulti("dietary", OPTIONS.dietary, state.dietary),
      valid: () => true,
      optional: true,
    },

    {
      id: "avoids",
      title: "Any dealbreakers?",
      desc: "Optional — ingredients and types to avoid.",
      render: () => avoidsScreen(),
      valid: () => true,
      optional: true,
    },

    {
      id: "basket",
      title: "Pick your kit",
      desc: "Choose your basket size.",
      render: () => tileRadioStack("budget_tier", OPTIONS.baskets, state.budget_tier),
      valid: () => !!state.budget_tier,
    },

    {
      id: "wine",
      title: "Would you like a wine pairing?",
      desc: "Optional — you can add wine to each shipment.",
      render: () => tileRadioStack("wine_pairing", OPTIONS.wine, state.wine_pairing),
      valid: () => !!state.wine_pairing, // default is "none"
      optional: true,
    },

    {
      id: "cadence",
      title: "Choose your schedule",
      desc: "More frequent shipments save more.",
      render: () => cadenceScreenWithPricing(),
      valid: () => {
        if (!state.cadence) return false;
        try { getSellingPlanIdFromCadence(state.cadence); return true; } catch { return false; }
      },
    },

    {
      id: "delivery_method",
      title: "How would you like to receive your box?",
      desc: "Ship to your door or pick up in San Francisco.",
      render: () => tileRadioStack("delivery_method", OPTIONS.deliveryMethod, state.delivery_method),
      valid: () => !!state.delivery_method,
    },

    {
      id: "delivery_followup",
      title: () =>
        state.delivery_method === "pickup"
          ? "Sounds great! Which location would you like to pick up from?"
          : "No problem — do you live in or outside of California?",
      desc: () =>
        state.delivery_method === "pickup"
          ? "We’ll have your box ready at the location you select."
          : "This helps us route your subscription correctly.",
      render: () => deliveryFollowupScreen(),
      valid: () => {
        if (state.delivery_method === "ship") return !!state.ca_residency;
        if (state.delivery_method === "pickup") return !!state.pickup_location;
        return false;
      },
    },

    {
      id: "visual",
      title: "Rate a few spreads",
      desc: "Optional — helps your curator nail the vibe.",
      render: () => visualRatingScreenPaged(),
      valid: () => true,
      optional: true,
    },

    {
      id: "summary",
      title: "Review your Provisions Club profile",
      desc: "Confirm everything looks right, then proceed to checkout.",
      render: () => summaryScreen(),
      valid: () => state.summary_confirm === true,
      submit: true,
    },
  ];

  const SIDEBAR_STEPS = steps.map((s) => ({
    id: s.id,
    label:
      s.id === "intro" ? "Overview" :
      s.id === "curated_for" ? "Who it’s for" :
      s.id === "goals" ? "What excites you" :
      s.id === "categories" ? "What sounds good" :
      s.id === "flavor" ? "Taste" :
      s.id === "dietary" ? "Dietary" :
      s.id === "avoids" ? "Dealbreakers" :
      s.id === "basket" ? "Kit" :
      s.id === "wine" ? "Wine" :
      s.id === "cadence" ? "Schedule" :
      s.id === "delivery_method" ? "Receive your box" :
      s.id === "delivery_followup" ? "Pickup / shipping" :
      s.id === "visual" ? "Rate spreads" :
      s.id === "summary" ? "Review" :
      "Step",
  }));

  function isStepComplete(stepId) {
    const stepIndex = steps.findIndex((s) => s.id === stepId);
    if (stepIndex < 0) return false;
    if (stepIndex >= idx) return false;
    const step = steps[stepIndex];
    try {
      return step.valid ? !!step.valid() : true;
    } catch {
      return false;
    }
  }

  let idx = 0;
  let submitting = false;

  function render() {
    const step = steps[idx];
    const progress = Math.round(((idx + 1) / steps.length) * 100);
    const canContinue = !submitting && (step.valid ? step.valid() : true);

    const isIntro = step.id === "intro";
    const titleText = typeof step.title === "function" ? step.title() : step.title;
    const descText = typeof step.desc === "function" ? step.desc() : step.desc;

    ui.innerHTML = `
      <style>
        *, *::before, *::after { box-sizing: border-box; }
        html, body { max-width: 100%; overflow-x: hidden; }
        #pc-quiz, #pc-quiz-ui { max-width: 100%; overflow-x: hidden; }
        img { max-width: 100%; height: auto; }

        .pcq-wrap{max-width:1040px;margin:0 auto;padding:10px 16px 28px;}
        @media(max-width:700px){ .pcq-wrap{ padding:10px 12px 28px; } }

        /* ---- top bar (no overflow on mobile) ---- */
        .pcq-sticky{
          position:sticky; top:0; z-index:30;
          background:#fff;
          padding:10px 0;
          border-bottom:1px solid rgba(0,0,0,.06);
        }
        .pcq-top{
          display:flex; align-items:center; gap:12px;
          min-width:0;
        }
        .pcq-progress{
          flex:1 1 auto; min-width:0;
          height:10px;border-radius:999px;background:rgba(0,0,0,.08);overflow:hidden;
        }
        .pcq-progress>div{height:100%;width:${progress}%;background:rgba(0,0,0,.90);}
        .pcq-step{flex:0 0 auto;font-size:13px;opacity:.7;white-space:nowrap;}
        @media(max-width:480px){
          .pcq-step{display:none;}
          .pcq-sticky{padding:8px 0;}
        }

        /* ---- layout ---- */
        .pcq-layout{
          display:grid;
          grid-template-columns:220px minmax(0,1fr);
          gap:18px;
          align-items:start;
          min-width:0;
        }
        @media(max-width:900px){
          .pcq-layout{ grid-template-columns:1fr; gap:12px; }
        }

        /* ---- sidebar ---- */
        .pcq-sidebar{
          position:sticky; top:64px;
          border:1px solid rgba(0,0,0,.10);
          border-radius:16px;
          padding:12px;
          background:#fff;
          box-shadow:0 8px 24px rgba(0,0,0,.04);
          min-width:0;
        }
        .pcq-side-title{
          font-size:12px;
          letter-spacing:.14em;
          text-transform:uppercase;
          opacity:.65;
          margin:2px 0 10px;
        }
        .pcq-side-list{
          display:grid; gap:8px; margin:0; padding:0; list-style:none; min-width:0;
        }
        .pcq-side-item{
          display:flex; gap:10px; align-items:flex-start;
          padding:8px 10px; border-radius:12px;
          border:1px solid rgba(0,0,0,.06);
          background:#fff; min-width:0;
        }
        .pcq-side-item[data-current="true"]{
          border-color: rgba(0,0,0,.20);
          box-shadow:0 0 0 2px rgba(0,0,0,.06) inset;
        }
        .pcq-side-item span.label{
          font-size:13px; line-height:1.25; opacity:.9; min-width:0;
          overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
        }
        .pcq-check{
          width:18px;height:18px;border-radius:6px;
          border:1px solid rgba(0,0,0,.20);
          display:inline-flex;align-items:center;justify-content:center;
          flex:0 0 auto; margin-top:1px; background:#fff;
        }
        .pcq-check svg{width:14px;height:14px;display:block;}
        .pcq-check[data-done="true"]{background:rgba(0,0,0,.90);border-color:rgba(0,0,0,.90);}
        .pcq-check[data-done="true"] svg path{fill:#fff;}
        .pcq-check[data-done="false"] svg path{fill:transparent;}

        /* mobile: sidebar becomes chips row */
        @media(max-width:900px){
          .pcq-sidebar{ position:sticky; top:56px; z-index:25; border-radius:14px; padding:10px; }
          .pcq-side-title{ display:none; }
          .pcq-side-list{
            display:flex; gap:8px; overflow:auto;
            -webkit-overflow-scrolling:touch; padding-bottom:2px;
          }
          .pcq-side-item{ flex:0 0 auto; align-items:center; padding:8px 10px; border-radius:999px; }
          .pcq-check{width:16px;height:16px;border-radius:6px;margin-top:0;}
          .pcq-side-item span.label{font-size:12px;white-space:nowrap;}
        }

        /* ---- card ---- */
        .pcq-card{
          border:1px solid rgba(0,0,0,.10);
          border-radius:18px;
          padding:16px;
          box-shadow:0 8px 24px rgba(0,0,0,.06);
          background:#fff;
          min-width:0;

          /* ✅ make content scroll inside, keep actions visible */
          display:flex;
          flex-direction:column;
          max-height: calc(100svh - 150px);
          overflow:hidden;
        }
        @media(max-width:900px){
          .pcq-card{ max-height: calc(100svh - 170px); }
        }
        @media(max-width:700px){
          .pcq-card{ padding:14px; border-radius:16px; max-height: calc(100svh - 160px); }
        }

        .pcq-title{font-size:26px;margin:8px 0 6px;letter-spacing:-.01em;}
        @media(max-width:700px){.pcq-title{font-size:22px;}}
        .pcq-desc{margin:0 0 14px;opacity:.82;font-size:15px;line-height:1.4}

        /* ✅ scrollable content area */
        .pcq-content{
          min-height:0;
          overflow:auto;
          padding-right:2px;
          -webkit-overflow-scrolling:touch;
        }

        /* ✅ sticky actions so Continue is always visible */
        .pcq-actions{
          margin-top:14px;
          padding-top:12px;
          border-top:1px solid rgba(0,0,0,.06);

          position:sticky;
          bottom:0;
          background:#fff;
          z-index:5;

          display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;
        }

        .pcq-right{display:flex;gap:12px;align-items:center;flex-wrap:wrap;}
        @media(max-width:520px){
          .pcq-right{width:100%;justify-content:flex-end;}
          .pcq-btn.primary{width:100%;}
        }
        .pcq-link{background:none;border:none;padding:8px 0;cursor:pointer;opacity:.75;font-size:14px;}
        .pcq-link:hover{opacity:1;text-decoration:underline;}
        .pcq-btn{border-radius:999px;padding:12px 16px;cursor:pointer;border:1px solid rgba(0,0,0,.14);background:#fff;}
        .pcq-btn.primary{background:rgba(0,0,0,.90);color:#fff;border-color:rgba(0,0,0,.90);}
        .pcq-btn:disabled{opacity:.45;cursor:not-allowed;}
        .pcq-error{min-height:18px;margin-top:10px;color:#b00020;font-size:14px;}

        /* ---- grids / tiles ---- */
        .pcq-grid{display:grid;gap:12px;min-width:0;}
        .pcq-grid-2{grid-template-columns:repeat(2,minmax(0,1fr));}
        .pcq-grid-1{grid-template-columns:1fr;}
        @media(max-width:700px){.pcq-grid-2{grid-template-columns:1fr;}}

        .pcq-tile{border:1px solid rgba(0,0,0,.14);border-radius:16px;overflow:hidden;cursor:pointer;background:#fff;min-width:0;}
        .pcq-tile[data-checked="true"]{border-color:rgba(0,0,0,.90);box-shadow:0 0 0 2px rgba(0,0,0,.10) inset;}
        .pcq-tile-img{height:132px;background:#f3f3f3;overflow:hidden;}
        @media(max-width:520px){.pcq-tile-img{height:110px;}}
        .pcq-tile-img img{width:100%;height:100%;object-fit:cover;display:block;}
        .pcq-tile-body{padding:12px;}
        .pcq-tile h4{margin:0 0 4px;font-size:16px;}
        .pcq-tile p{margin:0;opacity:.75;font-size:13px;line-height:1.35;}

        /* ✅ tighten tiles on category step to reduce height */
        [data-step="categories"] .pcq-tile-img{height:96px;}
        [data-step="categories"] .pcq-tile-body{padding:10px;}
        [data-step="categories"] .pcq-tile h4{font-size:15px;}

        /* ---- chips ---- */
        .pcq-chips{display:flex;flex-wrap:wrap;gap:10px;}
        .pcq-chip{border:1px solid rgba(0,0,0,.14);border-radius:999px;padding:10px 12px;cursor:pointer;display:flex;gap:10px;align-items:center;user-select:none;}
        .pcq-chip input{accent-color:#111;}
        .pcq-chip[data-checked="true"]{border-color:rgba(0,0,0,.90);}

        /* ---- sliders (easier drag) ---- */
        .pcq-slider{margin:12px 0;}
        .pcq-slider label{display:flex;justify-content:space-between;gap:12px;font-size:14px;opacity:.9}
        .pcq-slider input{width:100%;margin-top:8px;}

        .pcq-slider input[type="range"]{
          height: 36px;
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          touch-action: pan-y;
        }
        .pcq-slider input[type="range"]::-webkit-slider-runnable-track{
          height: 7px; border-radius: 999px; background: rgba(0,0,0,.18);
        }
        .pcq-slider input[type="range"]::-moz-range-track{
          height: 7px; border-radius: 999px; background: rgba(0,0,0,.18);
        }
        .pcq-slider input[type="range"]::-webkit-slider-thumb{
          -webkit-appearance: none;
          width: 26px; height: 26px; border-radius: 999px;
          background: rgba(0,0,0,.90);
          border: 2px solid #fff;
          margin-top: -9.5px;
        }
        .pcq-slider input[type="range"]::-moz-range-thumb{
          width: 26px; height: 26px; border-radius: 999px;
          background: rgba(0,0,0,.90);
          border: 2px solid #fff;
        }

        select.pcq-select{width:100%;border-radius:14px;padding:12px;border:1px solid rgba(0,0,0,.14);background:#fff;}
        textarea.pcq-textarea{width:100%;min-height:92px;border-radius:14px;padding:10px;border:1px solid rgba(0,0,0,.14);}

        /* ---- scenes ---- */
        .pcq-scene{border:1px solid rgba(0,0,0,.12);border-radius:16px;overflow:hidden;margin-bottom:12px;}
        .pcq-scene img{width:100%;height:220px;object-fit:cover;display:block;background:#f3f3f3;}
        @media(max-width:520px){.pcq-scene img{height:180px;}}
        .pcq-scene-meta{padding:12px;}
        .pcq-rate-row{display:flex;gap:10px;flex-wrap:wrap;margin-top:10px;}
        .pcq-rate-btn{border:1px solid rgba(0,0,0,.14);border-radius:999px;padding:10px 12px;background:#fff;cursor:pointer;}
        .pcq-rate-btn[data-active="true"]{border-color:rgba(0,0,0,.90);}

        /* ✅ keep visual step compact: show 1 scene per page + nav */
        .pcq-scene-nav{display:flex;justify-content:space-between;align-items:center;gap:12px;margin:8px 0 2px;}
        .pcq-mini{font-size:12px;opacity:.7;}

        /* ---- two-col step ---- */
        .pcq-two-col{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.15fr);gap:16px;align-items:stretch;min-width:0;}
        .pcq-two-col-img{border-radius:16px;overflow:hidden;border:1px solid rgba(0,0,0,.10);background:#f3f3f3;min-height:260px;min-width:0;}
        .pcq-two-col-img img{width:100%;height:100%;object-fit:cover;display:block;}
        .pcq-stack{display:grid;gap:12px;min-width:0;}
        @media(max-width:800px){
          .pcq-two-col{grid-template-columns:1fr;}
          .pcq-two-col-img{min-height:180px;}
        }

        /* ---- intro ---- */
        .pcq-intro-hero{border-radius:16px;overflow:hidden;border:1px solid rgba(0,0,0,.10);background:#f3f3f3;margin-bottom:14px;}
        .pcq-intro-hero img{width:100%;height:320px;object-fit:cover;display:block;}
        @media(max-width:700px){.pcq-intro-hero img{height:220px;}}
        .pcq-kicker{font-size:12px;letter-spacing:.14em;text-transform:uppercase;opacity:.7;margin-bottom:8px;}
        .pcq-intro-title{font-size:28px;letter-spacing:-.02em;margin:0 0 10px;}
        @media(max-width:700px){.pcq-intro-title{font-size:22px;}}
        .pcq-bullets{margin:0;padding-left:18px;opacity:.86;line-height:1.55;}
        .pcq-bullets li{margin:6px 0;}
        .pcq-intro-note{margin-top:12px;opacity:.78;font-size:14px;line-height:1.45;}

        /* ---- summary ---- */
        .pcq-summary{
          border:1px solid rgba(0,0,0,.10);
          border-radius:16px;
          padding:14px;
          background:rgba(0,0,0,.02);
        }
        .pcq-summary-row{
          display:flex; justify-content:space-between; gap:12px;
          padding:10px 0; border-bottom:1px solid rgba(0,0,0,.06); min-width:0;
        }
        .pcq-summary-row:last-child{border-bottom:none;}
        .pcq-summary-k{opacity:.7;font-size:13px;flex:0 0 auto;}
        .pcq-summary-v{font-size:14px;opacity:.92;text-align:right;min-width:0;overflow:hidden;text-overflow:ellipsis;}
        .pcq-summary-price{
          margin-top:12px; padding:12px;
          border:1px solid rgba(0,0,0,.10); border-radius:14px; background:#fff;
          display:flex; justify-content:space-between; gap:12px; align-items:center;
        }
        .pcq-summary-price strong{font-size:16px;}
        .pcq-confirm{ margin-top:12px; display:flex; gap:10px; align-items:flex-start; font-size:14px; opacity:.92; }
        .pcq-confirm input{margin-top:3px;accent-color:#111;}

        /* ---- cadence pricing box ---- */
        .pcq-pricebox{
          margin-top:12px;
          padding:12px;
          border:1px solid rgba(0,0,0,.10);
          border-radius:14px;
          background:rgba(0,0,0,.02);
        }
        .pcq-pricebox .row{
          display:flex; justify-content:space-between; gap:12px;
          padding:6px 0;
          border-bottom:1px solid rgba(0,0,0,.06);
        }
        .pcq-pricebox .row:last-child{border-bottom:none;}
        .pcq-pricebox .k{opacity:.7;font-size:13px;}
        .pcq-pricebox .v{font-size:13px;opacity:.9;text-align:right;}
      </style>

      <div class="pcq-wrap" data-step="${escapeHtml(step.id)}">
        <div class="pcq-sticky">
          <div class="pcq-top">
            <div class="pcq-progress"><div></div></div>
            <div class="pcq-step">Step ${idx + 1} of ${steps.length}</div>
          </div>
        </div>

        <div class="pcq-layout">
          ${renderSidebar()}

          <div class="pcq-card">
            ${!isIntro ? `<h2 class="pcq-title">${escapeHtml(titleText)}</h2>` : ""}
            ${!isIntro && descText ? `<p class="pcq-desc">${escapeHtml(descText)}</p>` : ""}

            <div class="pcq-content">${step.render()}</div>
            <div class="pcq-error" data-error></div>

            <div class="pcq-actions">
              <button class="pcq-link" type="button" data-back ${idx === 0 || submitting ? "disabled" : ""}>← Back</button>
              <div class="pcq-right">
                ${
                  step.optional && !step.submit && step.id !== "intro"
                    ? `<button class="pcq-link" type="button" data-skip ${submitting ? "disabled" : ""}>Skip</button>`
                    : ""
                }
                <button class="pcq-btn primary" type="button" data-next ${canContinue ? "" : "disabled"}>
                  ${
                    step.submit
                      ? submitting
                        ? "Submitting…"
                        : "Continue to checkout"
                      : step.id === "intro"
                        ? "Start the quiz"
                        : "Continue"
                  }
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    `;

    bindShell();
  }

  function renderSidebar() {
    const items = SIDEBAR_STEPS.map((s) => {
      const stepIndex = steps.findIndex((x) => x.id === s.id);
      const current = stepIndex === idx;
      const done = isStepComplete(s.id);

      return `
        <li class="pcq-side-item" data-current="${String(current)}">
          <span class="pcq-check" data-done="${String(done)}" aria-hidden="true">
            <svg viewBox="0 0 20 20" role="img" focusable="false" aria-hidden="true">
              <path d="M7.6 13.6 4.4 10.4l-1.3 1.3 4.5 4.5L17 6.7l-1.3-1.3z"></path>
            </svg>
          </span>
          <span class="label">${escapeHtml(s.label)}</span>
        </li>
      `;
    }).join("");

    return `
      <aside class="pcq-sidebar" aria-label="Quiz progress">
        <div class="pcq-side-title">Your quiz</div>
        <ul class="pcq-side-list">${items}</ul>
      </aside>
    `;
  }

  function setError(msg) {
    const el = ui.querySelector("[data-error]");
    if (el) el.textContent = msg || "";
  }

  function bindShell() {
    const step = steps[idx];

    ui.querySelector("[data-back]")?.addEventListener("click", () => {
      if (submitting) return;
      idx = Math.max(0, idx - 1);
      setError("");
      render();
    });

    ui.querySelector("[data-skip]")?.addEventListener("click", () => {
      if (submitting) return;
      idx = Math.min(steps.length - 1, idx + 1);
      setError("");
      render();
    });

    ui.querySelector("[data-next]")?.addEventListener("click", async () => {
      if (submitting) return;

      // If we’re at summary, ensure variant is resolved so we can display correct price.
      if (step.id === "summary") {
        await resolveSelectedVariant().catch(() => null);
      }

      if (step.submit) {
        const ok = step.valid ? step.valid() : true;
        if (!ok) return setError("Please confirm to continue.");
        await submit();
        return;
      }

      const ok = step.valid ? step.valid() : true;
      if (!ok) return setError("Please complete this step.");
      setError("");
      idx = Math.min(steps.length - 1, idx + 1);
      render();
    });
  }

  function refreshContinueState() {
    const step = steps[idx];
    const btn = ui.querySelector("[data-next]");
    if (!btn) return;
    const can = !submitting && (step.valid ? step.valid() : true);
    btn.disabled = !can;
  }

  // ---- Components
  function introScreen() {
    return `
      <div>
        <div class="pcq-intro-hero">
          <img loading="eager" alt="Provisions Club" src="${escapeHtml(INTRO.hero)}">
        </div>

        <div class="pcq-kicker">${escapeHtml(INTRO.kicker)}</div>
        <h2 class="pcq-intro-title">${escapeHtml(INTRO.title)}</h2>

        <ul class="pcq-bullets">
          ${INTRO.bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join("")}
        </ul>

        <div class="pcq-intro-note">${escapeHtml(INTRO.note)}</div>
      </div>
    `;
  }

  function tileRadio(field, options, value) {
    setTimeout(() => {
      ui.querySelectorAll(`[data-tile="${CSS.escape(field)}"]`).forEach((el) => {
        const act = async () => {
          const v = el.getAttribute("data-value");
          state[field] = v;
          ui.querySelectorAll(`[data-tile="${CSS.escape(field)}"]`).forEach((x) => {
            x.setAttribute("data-checked", String(x.getAttribute("data-value") === v));
          });
          setError("");
          refreshContinueState();
          await resolveSelectedVariant().catch(() => null);
        };
        el.addEventListener("click", act);
        el.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); act(); }
        });
      });
    }, 0);

    return `<div class="pcq-grid pcq-grid-2">${
      options.map((o) => `
        <div class="pcq-tile" tabindex="0"
          data-tile="${escapeHtml(field)}"
          data-value="${escapeHtml(o.value)}"
          data-checked="${String(o.value === value)}">
          <div class="pcq-tile-body">
            <h4>${escapeHtml(o.label)}</h4>
            <p>${escapeHtml(o.sub || "")}</p>
          </div>
        </div>
      `).join("")
    }</div>`;
  }

  function tileRadioStack(field, options, value) {
    setTimeout(() => {
      ui.querySelectorAll(`[data-tile="${CSS.escape(field)}"]`).forEach((el) => {
        const act = async () => {
          const v = el.getAttribute("data-value");
          state[field] = v;

          if (field === "delivery_method") {
            state.ca_residency = "";
            state.pickup_location = "";
          }

          ui.querySelectorAll(`[data-tile="${CSS.escape(field)}"]`).forEach((x) => {
            x.setAttribute("data-checked", String(x.getAttribute("data-value") === v));
          });

          setError("");
          refreshContinueState();

          // Try resolve variant whenever relevant selection changes
          await resolveSelectedVariant().catch(() => null);
        };

        el.addEventListener("click", act);
        el.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); act(); }
        });
      });
    }, 0);

    return `<div class="pcq-grid pcq-grid-1">${
      options.map((o) => `
        <div class="pcq-tile" tabindex="0"
          data-tile="${escapeHtml(field)}"
          data-value="${escapeHtml(o.value)}"
          data-checked="${String(o.value === value)}">
          <div class="pcq-tile-body">
            <h4>${escapeHtml(o.label)}</h4>
            ${o.sub ? `<p>${escapeHtml(o.sub)}</p>` : ""}
          </div>
        </div>
      `).join("")
    }</div>`;
  }

  function twoColumnImageMulti(field, options, values, imageUrl) {
    setTimeout(() => {
      ui.querySelectorAll(`[data-mtile="${CSS.escape(field)}"]`).forEach((el) => {
        const toggle = () => {
          const id = el.getAttribute("data-id");
          let list = Array.isArray(state[field]) ? [...state[field]] : [];
          const isOn = list.includes(id);
          setError("");
          list = isOn ? list.filter((x) => x !== id) : [...list, id];
          state[field] = uniq(list);
          el.setAttribute("data-checked", String(!isOn));
          refreshContinueState();
        };
        el.addEventListener("click", toggle);
        el.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
        });
      });
    }, 0);

    return `
      <div class="pcq-two-col">
        <div class="pcq-two-col-img">
          <img loading="lazy" alt="Provisions Club" src="${escapeHtml(imageUrl)}">
        </div>
        <div class="pcq-stack">
          ${options.map((o) => `
            <div class="pcq-tile" tabindex="0"
              data-mtile="${escapeHtml(field)}"
              data-id="${escapeHtml(o.id)}"
              data-checked="${values.includes(o.id)}">
              <div class="pcq-tile-body">
                <h4>${escapeHtml(o.label)}</h4>
                ${o.sub ? `<p>${escapeHtml(o.sub)}</p>` : ""}
              </div>
            </div>
          `).join("")}
        </div>
      </div>
    `;
  }

  function tileMultiWithSceneImages(field, options, values, max) {
    const imgById = new Map();
    for (const s of SCENES) imgById.set(s.id, s.image);

    setTimeout(() => {
      ui.querySelectorAll(`[data-mtile="${CSS.escape(field)}"]`).forEach((el) => {
        const toggle = () => {
          const id = el.getAttribute("data-id");
          let list = Array.isArray(state[field]) ? [...state[field]] : [];
          const isOn = list.includes(id);

          if (!isOn && max && list.length >= max) return setError(`Pick up to ${max}.`);
          setError("");
          list = isOn ? list.filter((x) => x !== id) : [...list, id];
          state[field] = uniq(list);
          el.setAttribute("data-checked", String(!isOn));
          refreshContinueState();
        };

        el.addEventListener("click", toggle);
        el.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
        });
      });
    }, 0);

    return `<div class="pcq-grid pcq-grid-2">${
      options.map((o) => {
        const img = imgById.get(o.id);
        return `
          <div class="pcq-tile" tabindex="0"
            data-mtile="${escapeHtml(field)}"
            data-id="${escapeHtml(o.id)}"
            data-checked="${values.includes(o.id)}">
            ${
              img
                ? `<div class="pcq-tile-img"><img loading="lazy" alt="${escapeHtml(o.label)}" src="${escapeHtml(img)}"></div>`
                : ""
            }
            <div class="pcq-tile-body">
              <h4>${escapeHtml(o.label)}</h4>
              <p>${escapeHtml(o.sub || "")}</p>
            </div>
          </div>
        `;
      }).join("")
    }</div>`;
  }

  function chipMulti(field, options, values) {
    setTimeout(() => {
      ui.querySelectorAll(`input[data-chip="${CSS.escape(field)}"]`).forEach((cb) => {
        cb.addEventListener("change", () => {
          const val = cb.value;
          let list = Array.isArray(state[field]) ? [...state[field]] : [];
          list = cb.checked ? [...list, val] : list.filter((x) => x !== val);
          state[field] = uniq(list);
          cb.closest(".pcq-chip")?.setAttribute("data-checked", String(cb.checked));
          refreshContinueState();
        });
      });
    }, 0);

    return `<div class="pcq-chips">${
      options.map((label) => {
        const checked = values.includes(label);
        return `
          <label class="pcq-chip" data-checked="${checked}">
            <input type="checkbox" data-chip="${escapeHtml(field)}" value="${escapeHtml(label)}" ${checked ? "checked" : ""}/>
            <span>${escapeHtml(label)}</span>
          </label>
        `;
      }).join("")
    }</div>`;
  }

  function flavorScreen() {
    // 0..100 smoother on touch
    const storedToVal = (s) => {
      if (s === "Sweet" || s === "Mild") return 0;
      if (s === "Savory" || s === "Bold") return 100;
      return 50;
    };

    const valToStored = (v, left, right) => {
      const n = Number(v);
      if (n <= 35) return left;
      if (n >= 65) return right;
      return "Balanced";
    };

    setTimeout(() => {
      ui.querySelectorAll('input[type="range"][data-axis]').forEach((rng) => {
        rng.addEventListener("input", () => {
          const axis = rng.getAttribute("data-axis");
          const left = rng.getAttribute("data-left");
          const right = rng.getAttribute("data-right");
          const stored = valToStored(rng.value, left, right);
          state.flavor[axis] = stored;

          const live = rng.closest(".pcq-slider")?.querySelector("[data-live]");
          if (live) live.textContent = stored;

          refreshContinueState();
        });
      });

      const spiceSel = ui.querySelector('select[data-field="spice"]');
      if (spiceSel) {
        spiceSel.addEventListener("change", (e) => { state.spice = e.target.value; });
      }
    }, 0);

    const sweetSavoryLabel = state.flavor.sweet_savory || "Balanced";
    const mildBoldLabel = state.flavor.bold_mild || "Balanced";

    return `
      <div>
        <div class="pcq-slider">
          <label>
            <span>Sweet ↔ Savory</span>
            <span data-live style="opacity:.75">${escapeHtml(sweetSavoryLabel)}</span>
          </label>
          <input type="range" min="0" max="100" step="1"
            data-axis="sweet_savory" data-left="Sweet" data-right="Savory"
            value="${storedToVal(state.flavor.sweet_savory)}" />
        </div>

        <div class="pcq-slider">
          <label>
            <span>Mild ↔ Bold</span>
            <span data-live style="opacity:.75">${escapeHtml(mildBoldLabel)}</span>
          </label>
          <input type="range" min="0" max="100" step="1"
            data-axis="bold_mild" data-left="Mild" data-right="Bold"
            value="${storedToVal(state.flavor.bold_mild)}" />
        </div>

        <div style="margin-top:14px;">
          <label style="display:block;margin-bottom:8px;font-size:14px;opacity:.9;">Spice level (optional)</label>
          <select class="pcq-select" data-field="spice">
            <option value="">Select one</option>
            ${OPTIONS.spice.map((o) =>
              `<option value="${escapeHtml(o.value)}" ${o.value === state.spice ? "selected" : ""}>${escapeHtml(o.label)}</option>`
            ).join("")}
          </select>
        </div>
      </div>
    `;
  }

  function avoidsScreen() {
    return `
      <div class="pcq-grid" style="gap:16px;">
        <div>
          <h3 style="margin:0 0 8px;font-size:16px;">Ingredients</h3>
          ${chipMulti("avoid_ingredients", OPTIONS.avoidIngredients, state.avoid_ingredients)}
        </div>
        <div>
          <h3 style="margin:0 0 8px;font-size:16px;">Item types</h3>
          ${chipMulti("avoid_types", OPTIONS.avoidTypes, state.avoid_types)}
        </div>
      </div>
    `;
  }

  function deliveryFollowupScreen() {
    if (state.delivery_method === "ship") {
      return tileRadioStack("ca_residency", OPTIONS.caResidency, state.ca_residency);
    }
    if (state.delivery_method === "pickup") {
      const opts = OPTIONS.pickupLocations.map((o) => ({ value: o.value, label: o.label }));
      return tileRadioStack("pickup_location", opts, state.pickup_location);
    }
    return `<p style="opacity:.75;margin:0;">Choose a delivery method to continue.</p>`;
  }

  function cadenceScreenWithPricing() {
    // Resolve variant if we can, so we can show the exact variant price as well.
    setTimeout(async () => {
      await resolveSelectedVariant().catch(() => null);
      refreshContinueState();
    }, 0);

    setTimeout(() => {
      ui.querySelectorAll(`[data-tile="cadence"]`).forEach((el) => {
        const act = async () => {
          const v = el.getAttribute("data-value");
          state.cadence = v;
          ui.querySelectorAll(`[data-tile="cadence"]`).forEach((x) => {
            x.setAttribute("data-checked", String(x.getAttribute("data-value") === v));
          });
          setError("");
          refreshContinueState();
          await resolveSelectedVariant().catch(() => null);
          render();
        };
        el.addEventListener("click", act);
        el.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); act(); }
        });
      });

      ui.querySelector("textarea.pcq-textarea")?.addEventListener("input", (e) => {
        state.notes = e.target.value;
      });
    }, 0);

    const { base, wine, ship, subtotal, discount, total, pct } = computeDisplayTotalCents();
    const variantExact = state.selected_variant_price_cents != null ? formatMoneyFromCents(state.selected_variant_price_cents) : "";

    return `
      <div>
        <div class="pcq-grid pcq-grid-2">${
          OPTIONS.cadence.map((c) => {
            const checked = state.cadence === c.value;
            const missing = !Number((SELLING_PLANS && SELLING_PLANS[c.value]) || 0);
            return `
              <div class="pcq-tile" tabindex="0" data-tile="cadence" data-value="${escapeHtml(c.value)}" data-checked="${checked}">
                <div class="pcq-tile-body">
                  <h4>${escapeHtml(c.label)}</h4>
                  <p>${escapeHtml(missing ? "Not configured" : c.sub || "Flexible cadence.")}</p>
                </div>
              </div>
            `;
          }).join("")
        }</div>

        <div class="pcq-pricebox" role="note" aria-label="Estimated pricing">
          <div style="font-size:12px;letter-spacing:.14em;text-transform:uppercase;opacity:.65;margin-bottom:8px;">
            Your estimated per-shipment total
          </div>

          <div class="row"><div class="k">Base kit</div><div class="v">${escapeHtml(formatMoneyFromCents(base))}</div></div>
          <div class="row"><div class="k">Wine pairing</div><div class="v">${escapeHtml(formatMoneyFromCents(wine))}</div></div>
          <div class="row"><div class="k">Shipping / pickup</div><div class="v">${escapeHtml(formatMoneyFromCents(ship))}</div></div>
          <div class="row"><div class="k">Subtotal</div><div class="v">${escapeHtml(formatMoneyFromCents(subtotal))}</div></div>
          <div class="row"><div class="k">Cadence savings</div><div class="v">${pct ? `- ${escapeHtml(formatMoneyFromCents(discount))} (${pct}%)` : "—"}</div></div>
          <div class="row"><div class="k"><strong>Total</strong></div><div class="v"><strong>${escapeHtml(formatMoneyFromCents(total))}</strong></div></div>

          ${
            variantExact
              ? `<div class="pcq-mini" style="margin-top:8px;">
                  Shopify variant price (before selling plan adjustments): <strong>${escapeHtml(variantExact)}</strong>
                </div>`
              : `<div class="pcq-mini" style="margin-top:8px;">
                  Select kit, wine, and shipping to lock in the exact variant.
                </div>`
          }
        </div>

        <div style="margin-top:14px;">
          <label style="display:block;margin-bottom:8px;font-size:14px;opacity:.9;">Anything else we should know?</label>
          <textarea class="pcq-textarea" placeholder="Upcoming event, favorite ingredient, dealbreakers, etc.">${escapeHtml(state.notes || "")}</textarea>
          <div style="opacity:.7;font-size:12px;margin-top:6px;">Optional — but helpful.</div>
        </div>
      </div>
    `;
  }

  // ✅ “Rate spreads” fits screen: 1 scene per page with Next/Prev inside content
  function visualRatingScreenPaged() {
    const spreadIds = ["pantry", "snacks", "pasta", "tinned", "hosting"];
    const spreads = SCENES.filter((s) => spreadIds.includes(s.id));

    if (!state._visual_page) state._visual_page = 0;
    const page = Math.max(0, Math.min(spreads.length - 1, Number(state._visual_page || 0)));
    const scene = spreads[page];

    setTimeout(() => {
      ui.querySelectorAll("[data-rate]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const sceneId = btn.getAttribute("data-scene");
          const rating = btn.getAttribute("data-rate");
          const list = Array.isArray(state.visual_feedback) ? [...state.visual_feedback] : [];
          const i = list.findIndex((x) => x.scene_id === sceneId);
          if (i >= 0) list[i] = { ...list[i], rating };
          else list.push({ scene_id: sceneId, rating });
          state.visual_feedback = list;
          render();
        });
      });

      ui.querySelector("[data-vprev]")?.addEventListener("click", () => {
        state._visual_page = Math.max(0, page - 1);
        render();
      });
      ui.querySelector("[data-vnext]")?.addEventListener("click", () => {
        state._visual_page = Math.min(spreads.length - 1, page + 1);
        render();
      });
    }, 0);

    if (!scene) {
      return `<div style="opacity:.75;">No scenes configured.</div>`;
    }

    const fb = (state.visual_feedback || []).find((x) => x.scene_id === scene.id);
    const rating = fb?.rating || "";

    return `
      <div>
        <div class="pcq-scene-nav">
          <button class="pcq-btn" type="button" data-vprev ${page === 0 ? "disabled" : ""}>← Previous</button>
          <div class="pcq-mini">Image ${page + 1} of ${spreads.length}</div>
          <button class="pcq-btn" type="button" data-vnext ${page === spreads.length - 1 ? "disabled" : ""}>Next →</button>
        </div>

        <div class="pcq-scene">
          <img loading="lazy" alt="${escapeHtml(scene.title)}" src="${escapeHtml(scene.image)}"/>
          <div class="pcq-scene-meta">
            <h4 style="margin:0 0 4px;font-size:16px;">${escapeHtml(scene.title)}</h4>
            <p style="margin:0;opacity:.75;font-size:13px;">${escapeHtml(scene.subtitle || "")}</p>
            <div class="pcq-rate-row">
              <button class="pcq-rate-btn" type="button" data-scene="${escapeHtml(scene.id)}" data-rate="love" data-active="${rating === "love"}">👍 Love it</button>
              <button class="pcq-rate-btn" type="button" data-scene="${escapeHtml(scene.id)}" data-rate="nope" data-active="${rating === "nope"}">👎 Not for me</button>
              <button class="pcq-rate-btn" type="button" data-scene="${escapeHtml(scene.id)}" data-rate="some" data-active="${rating === "some"}">✨ Some</button>
            </div>
          </div>
        </div>

        <div class="pcq-mini">Optional — you can continue anytime.</div>
      </div>
    `;
  }

  function summaryScreen() {
    setTimeout(async () => {
      // Resolve final variant for exact cart/price
      await resolveSelectedVariant().catch(() => null);

      const cb = ui.querySelector('input[data-summary-confirm="1"]');
      if (cb) {
        cb.addEventListener("change", () => {
          state.summary_confirm = !!cb.checked;
          refreshContinueState();
        });
      }
    }, 0);

    const { kit, wine, shipping } = getChosenOptionValues();
    const cadenceLabel = OPTIONS.cadence.find((c) => c.value === state.cadence)?.label || state.cadence || "";

    const display = computeDisplayTotalCents();
    const variantPrice =
      state.selected_variant_price_cents != null
        ? formatMoneyFromCents(state.selected_variant_price_cents)
        : "—";

    const listOrDash = (arr) => (arr && arr.length ? arr.join(", ") : "—");

    return `
      <div class="pcq-summary">
        <div class="pcq-summary-row"><div class="pcq-summary-k">Curating for</div><div class="pcq-summary-v">${escapeHtml(state.curated_for || "—")}</div></div>
        <div class="pcq-summary-row"><div class="pcq-summary-k">Excited about</div><div class="pcq-summary-v">${escapeHtml(listOrDash(state.goals))}</div></div>
        <div class="pcq-summary-row"><div class="pcq-summary-k">Sounds good</div><div class="pcq-summary-v">${escapeHtml(listOrDash(state.categories))}</div></div>
        <div class="pcq-summary-row"><div class="pcq-summary-k">Taste</div><div class="pcq-summary-v">${escapeHtml(`${state.flavor.sweet_savory || "Balanced"} / ${state.flavor.bold_mild || "Balanced"}${state.spice ? ` / spice: ${state.spice}` : ""}`)}</div></div>
        <div class="pcq-summary-row"><div class="pcq-summary-k">Dietary</div><div class="pcq-summary-v">${escapeHtml(listOrDash(state.dietary))}</div></div>
        <div class="pcq-summary-row"><div class="pcq-summary-k">Avoid</div><div class="pcq-summary-v">${escapeHtml([...(state.avoid_ingredients || []), ...(state.avoid_types || [])].length ? [...(state.avoid_ingredients || []), ...(state.avoid_types || [])].join(", ") : "—")}</div></div>
        <div class="pcq-summary-row"><div class="pcq-summary-k">Kit / Wine / Shipping</div><div class="pcq-summary-v">${escapeHtml(`${kit || "—"} / ${wine || "—"} / ${shipping || "—"}`)}</div></div>
        <div class="pcq-summary-row"><div class="pcq-summary-k">Schedule</div><div class="pcq-summary-v">${escapeHtml(cadenceLabel || "—")}</div></div>
      </div>

      <div class="pcq-summary-price" role="note" aria-label="Membership price">
        <div>
          <div style="font-size:12px;letter-spacing:.14em;text-transform:uppercase;opacity:.65;margin-bottom:4px;">
            Shopify variant price
          </div>
          <strong>${escapeHtml(variantPrice)}</strong>
          <div style="opacity:.7;font-size:12px;margin-top:3px;">Final total in checkout reflects your selling plan.</div>
        </div>
        <div style="opacity:.8;font-size:12px;max-width:280px;text-align:right;">
          Est. total with cadence savings: <strong>${escapeHtml(formatMoneyFromCents(display.total))}</strong>
        </div>
      </div>

      <label class="pcq-confirm">
        <input type="checkbox" data-summary-confirm="1" ${state.summary_confirm ? "checked" : ""}>
        <span>I confirm my preferences are accurate and I’m ready to proceed to checkout.</span>
      </label>

      ${
        (!state.selected_variant_id && (state.budget_tier || state.wine_pairing || state.delivery_method))
          ? `<div class="pcq-mini" style="color:#b00020;margin-top:10px;">
              We couldn’t match a Shopify variant for your selections. Please confirm Kit, Wine, and Shipping choices.
            </div>`
          : ""
      }
    `;
  }

  // ---- Submit payloads
  function buildProfile() {
    return {
      v: 1,
      created_at: new Date().toISOString(),
      curated_for: state.curated_for,
      goals: uniq(state.goals),
      categories: uniq(state.categories),
      flavor: { ...state.flavor },
      spice: state.spice || "",
      dietary: uniq(state.dietary),
      avoids: {
        ingredients: uniq(state.avoid_ingredients),
        product_types: uniq(state.avoid_types),
      },
      budget_tier: state.budget_tier || "",
      wine_pairing: state.wine_pairing || "none",
      cadence: state.cadence || "monthly",
      notes: state.notes || "",
      product_handle: PRODUCT_HANDLE,
      delivery_method: state.delivery_method || "",
      ca_residency: state.ca_residency || "",
      pickup_location: state.pickup_location || "",
      selected_variant_id: state.selected_variant_id || 0,
      selected_variant_price_cents: state.selected_variant_price_cents ?? null,
    };
  }

  function buildCartAttributes(profile) {
    return {
      pc_profile_json: JSON.stringify(profile),

      pc_curated_for: profile.curated_for || "",
      pc_goals: (profile.goals || []).slice(0, 24).join(", "),
      pc_categories: (profile.categories || []).slice(0, 24).join(", "),
      pc_spice: profile.spice || "",
      pc_dietary: (profile.dietary || []).slice(0, 48).join(", "),
      pc_avoids: (profile.avoids?.ingredients || []).slice(0, 48).join(", "),
      pc_avoid_types: (profile.avoids?.product_types || []).slice(0, 48).join(", "),

      pc_budget_tier: profile.budget_tier || "",
      pc_wine_pairing: profile.wine_pairing || "",
      pc_cadence: profile.cadence || "",

      pc_delivery_method: profile.delivery_method || "",
      pc_ca_residency: profile.ca_residency || "",
      pc_pickup_location: profile.pickup_location || "",

      pc_selected_variant_id: profile.selected_variant_id ? String(profile.selected_variant_id) : "",
      pc_selected_variant_price_cents:
        profile.selected_variant_price_cents != null ? String(profile.selected_variant_price_cents) : "",
    };
  }

  async function submit() {
    // Resolve final variant
    await resolveSelectedVariant().catch(() => null);

    const variantToAdd = Number(state.selected_variant_id || DEFAULT_VARIANT_ID || 0);
    if (!variantToAdd) return setError("Missing subscription variant id.");

    // Ensure shipping/wine/kit are chosen so we match a real variant
    if (!state.selected_variant_id) {
      return setError("Please complete Kit, Wine, and Shipping selections so we can match the right variant.");
    }

    let sellingPlanId;
    try {
      sellingPlanId = getSellingPlanIdFromCadence(state.cadence);
    } catch (e) {
      return setError(e.message);
    }

    submitting = true;
    render();

    try {
      const profile = buildProfile();
      const attributes = buildCartAttributes(profile);

      await clearCart();
      await setCartAttributes(attributes, "Provisions Club quiz completed");
      await addSubscriptionItem(variantToAdd, sellingPlanId, 1);

      window.location.href = `${SHOPIFY_ROOT}checkout`;
    } catch (e) {
      console.error(e);
      setError(e?.message || "Something went wrong. Please try again.");
      submitting = false;
      render();
    }
  }

  // Kickoff
  render();
  refreshContinueState();
})();
